import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/router"

import { useGetOrgRecordTransport } from "@/organization"
import { ReportRunLimitDialog } from "@/runs/multi-run"
import { Button, cn, TABLE_CARD_CLASS } from "@coval/ui"
import { Settings } from "lucide-react"

import {
  TRACKING_EVENTS,
  useAnalytics,
  useComponentLoadTime,
} from "@/lib/analytics"
import { usePageShortcuts } from "@/hooks/usePageShortcuts"
import { useQueryParams } from "@/hooks/useQueryParams"
import { useSetHeaderActions } from "@/hooks/useSetHeaderActions"

import { ConversationsErrorState, ConversationsTable } from "./components"
import { AudioUploadDropdown } from "./components/AudioUploadDropdown"
import { ConversationsFilterProvider } from "./contexts/ConversationsFilterContext"
import { ConversationsNavigationProvider } from "./contexts/ConversationsNavigationContext"
import { ConversationsPopoverProvider } from "./contexts/ConversationsPopoverContext"
import { useUploadDeepLink } from "./hooks/useUploadDeepLink"
import {
  buildUploadedReportSearchParams,
  isUploadedReportSelectionOverLimit,
  type UploadedReportSelection,
} from "./utils/uploadedReportSelection"

function UploadedConversationsContent() {
  const router = useRouter()
  const { slug } = router.query
  const queryParams = useQueryParams()
  const {
    data: organization_record,
    error: organizationError,
    isError: isOrganizationError,
    refetch: refetchOrganization,
  } = useGetOrgRecordTransport()
  const hasTrackedPageView = useRef(false)
  const [pendingReportSelection, setPendingReportSelection] =
    useState<UploadedReportSelection | null>(null)

  const [uploadRequestCount, setUploadRequestCount] = useState(0)
  const requestUpload = useCallback(
    () => setUploadRequestCount((count) => count + 1),
    [],
  )
  useUploadDeepLink(requestUpload)

  const { trackEvent } = useAnalytics()

  const navigateToReport = useCallback(
    (selection: UploadedReportSelection) => {
      const params = buildUploadedReportSearchParams(selection)
      const path = queryParams.getHref(
        `/reports/new?${params.toString()}`,
        slug as string,
      )
      void router.push(path)
    },
    [slug, queryParams, router],
  )
  const handleGenerateReport = useCallback(
    (selection: UploadedReportSelection) => {
      if (isUploadedReportSelectionOverLimit(selection)) {
        setPendingReportSelection(selection)
        return
      }
      navigateToReport(selection)
    },
    [navigateToReport],
  )
  const handleReportLimitConfirm = useCallback(() => {
    if (pendingReportSelection) {
      navigateToReport(pendingReportSelection)
    }
    setPendingReportSelection(null)
  }, [navigateToReport, pendingReportSelection])

  const handleConfigureMetrics = useCallback(() => {
    trackEvent(TRACKING_EVENTS.UPLOADED_CONVERSATIONS.CONFIGURE_METRICS_CLICKED)
    const href = queryParams.getHref(
      "/settings/workspaces/metrics/default",
      slug as string,
    )
    void router.push(href)
  }, [trackEvent, queryParams, slug, router])

  const handleOpenResults = useCallback(
    (simulationOutputId: string) => {
      const href = queryParams.getHref(
        `/uploaded/results/${simulationOutputId}`,
        slug as string,
      )
      void router.push(href)
    },
    [router, queryParams, slug],
  )

  const pageShortcuts = useMemo(
    () => [
      {
        action: handleConfigureMetrics,
        key: "m",
        label: "Configure default metrics",
      },
    ],
    [handleConfigureMetrics],
  )

  usePageShortcuts(pageShortcuts)

  // Primary page action: Upload Conversation, pinned to the top-right of the page
  // header (left of the Sofia toggle). Wrapped in its own overlay provider
  // since the header renders outside this page's provider tree. This is the
  // page's only upload dialog — the sidebar shortcut and the empty state both
  // raise the request count that opens it, so no two upload forms can run at
  // once, and a repeated request reopens it without remounting away a
  // half-filled form or an upload in flight.
  const headerActions = useMemo(
    () => ({
      type: "custom" as const,
      node: (
        <ConversationsPopoverProvider>
          <AudioUploadDropdown
            openRequestCount={uploadRequestCount}
            trigger={
              <Button variant="default" aria-label="Upload conversations">
                Upload Conversation
              </Button>
            }
          />
        </ConversationsPopoverProvider>
      ),
    }),
    [uploadRequestCount],
  )
  useSetHeaderActions(headerActions)

  // Track page load time
  useComponentLoadTime("monitoring_page", true, {
    organization_id: organization_record?.id,
    has_playground_open: false,
  })

  // Track page view on mount only
  useEffect(() => {
    if (hasTrackedPageView.current) {
      return
    }
    hasTrackedPageView.current = true

    trackEvent(TRACKING_EVENTS.UPLOADED_CONVERSATIONS.PAGE_VIEWED)
    trackEvent(TRACKING_EVENTS.UPLOADED_CONVERSATIONS.PAGE_LOADED, {
      organization_id: organization_record?.id,
      timestamp: new Date().toISOString(),
    })
  }, [organization_record?.id, trackEvent])

  // The table's keyset query stays disabled without an organization id, so a
  // failed organization record would otherwise hold the table on skeleton rows
  // with nothing to retry.
  if (isOrganizationError) {
    return (
      <div
        className={cn("w-full h-full min-w-0 flex flex-col", TABLE_CARD_CLASS)}
      >
        <ConversationsErrorState
          error={organizationError}
          onRetry={() => {
            void refetchOrganization()
          }}
        />
      </div>
    )
  }

  return (
    <>
      <ConversationsTable
        onRowClick={handleOpenResults}
        onReport={handleGenerateReport}
        onRequestUpload={requestUpload}
        organizationId={organization_record?.id || ""}
        searchActions={
          <Button
            type="button"
            variant="secondary"
            icon
            tooltip="Default Metrics"
            // Bottom: this row sits under the sticky navbar, which Radix does not treat as a collision edge.
            tooltipProps={{ side: "bottom" }}
            onClick={handleConfigureMetrics}
            aria-label="Configure default metrics"
          >
            <Settings aria-hidden />
          </Button>
        }
      />
      <ReportRunLimitDialog
        itemLabel="simulations"
        onCancel={() => setPendingReportSelection(null)}
        onContinue={handleReportLimitConfirm}
        open={pendingReportSelection !== null}
      />
    </>
  )
}

export function UploadedConversations() {
  return (
    <ConversationsNavigationProvider>
      <ConversationsFilterProvider>
        <UploadedConversationsContent />
      </ConversationsFilterProvider>
    </ConversationsNavigationProvider>
  )
}
